// src/components/MapPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkedAlt, faExclamationTriangle, faHome, faUsers } from '@fortawesome/free-solid-svg-icons';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api';
import { getFirestore, collection, getDocs, doc, getDoc, query, where } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';

import './MapPage.css';

// --- Configuration ---
const Maps_API_KEY_FROM_ENV = process.env.REACT_APP_MAP_API_KEY;
const HARDCODED_DEFAULT_MAP_CENTER = { lat: 35.5857, lng: -78.8011 };
const DEFAULT_MAP_ZOOM = 11;
const db = getFirestore();
const ORG_ICON_PATH = '/assets/church.svg';

/**
 * Calculate the distance in miles between two lat/lng points using Haversine formula
 */
function getDistanceMiles(lat1, lng1, lat2, lng2) {
    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
}

/**
 * Calculate appropriate zoom level to fit a given radius (in miles) from center
 * Based on Google Maps zoom levels where zoom 0 = whole world (~25000 miles)
 */
function getZoomForRadius(radiusMiles) {
    if (radiusMiles <= 0.5) return 14;
    if (radiusMiles <= 1) return 13;
    if (radiusMiles <= 2) return 12;
    if (radiusMiles <= 5) return 11;
    if (radiusMiles <= 10) return 10;
    if (radiusMiles <= 20) return 9;
    if (radiusMiles <= 40) return 8;
    if (radiusMiles <= 80) return 7;
    return 6;
}

/**
 * Calculate smart zoom to fit all markers (for small groups) or most markers (for large groups).
 * Always ensures the church and family markers are visible together.
 */
function calculateSmartZoom(centerLat, centerLng, families) {
    if (!families || families.length === 0) return DEFAULT_MAP_ZOOM;

    // Calculate distances from church (center) to each family
    const distances = families.map(f => getDistanceMiles(centerLat, centerLng, f.lat, f.lng));
    distances.sort((a, b) => a - b);

    // For small groups (≤ 10), show ALL markers. For larger groups, use 90th percentile.
    const percentile = families.length <= 10 ? 1.0 : 0.9;
    const percentileIndex = Math.max(0, Math.ceil(distances.length * percentile) - 1);
    const radiusToFit = distances[percentileIndex];

    // Add buffer so markers aren't right at the edge (15% for small groups, 20% for large)
    const bufferMultiplier = families.length <= 10 ? 1.15 : 1.2;
    const bufferedRadius = Math.max(radiusToFit * bufferMultiplier, 0.1); // Min 0.1 miles to avoid extreme zoom

    return getZoomForRadius(bufferedRadius);
}

function MapPage() {
    // --- State Variables ---
    const { userOrgId, loadingAuth, currentUser, isAdmin } = useAuth();
    const [groups, setGroups] = useState([]);
    const [selectedGroupId, setSelectedGroupId] = useState('');
    const [membersData, setMembersData] = useState([]);
    const [familiesData, setFamiliesData] = useState([]);
    const [organizationDetails, setOrganizationDetails] = useState(null);
    const [mapCenter, setMapCenter] = useState(HARDCODED_DEFAULT_MAP_CENTER);
    const [mapZoom, setMapZoom] = useState(DEFAULT_MAP_ZOOM);
    const [selectedFamily, setSelectedFamily] = useState(null);
    const [selectedOrganization, setSelectedOrganization] = useState(null);
    const [loadingGroups, setLoadingGroups] = useState(true);
    const [loadingMembers, setLoadingMembers] = useState(false);
    const [loadingFamilies, setLoadingFamilies] = useState(false);
    const [error, setError] = useState(null);

    // --- Google Maps API Loader ---
    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: Maps_API_KEY_FROM_ENV,
        id: 'google-map-script'
    });

    // --- Effects ---

    // Effect 1: Fetch Organization Details and Set Initial Map Center/Zoom
    useEffect(() => {
        if (userOrgId) {
            const fetchOrganizationCenter = async () => {
                try {
                    const orgDocRef = doc(db, 'organization', userOrgId);
                    const orgDocSnap = await getDoc(orgDocRef);
                    if (orgDocSnap.exists()) {
                        const orgData = orgDocSnap.data();
                        setOrganizationDetails({
                            id: orgDocSnap.id,
                            orgName: orgData.orgName || "Organization Location",
                            orgStreet: orgData.orgAddress,
                            orgCity: orgData.orgCity,
                            orgState: orgData.orgState,
                            orgZipcode: orgData.orgZip,
                            lat: orgData.lat,
                            lng: orgData.lng,
                            defaultZoom: orgData.defaultZoom,
                            ...orgData
                        });

                        if (orgData.lat != null && orgData.lng != null) {
                            const newOrgCenter = { lat: orgData.lat, lng: orgData.lng };
                            setMapCenter(newOrgCenter);
                            setMapZoom(orgData.defaultZoom || DEFAULT_MAP_ZOOM);
                        } else {
                            setMapCenter(HARDCODED_DEFAULT_MAP_CENTER);
                            setMapZoom(DEFAULT_MAP_ZOOM);
                        }
                    } else {
                        setOrganizationDetails({ orgName: "Default Location (Org Not Found)" });
                        setMapCenter(HARDCODED_DEFAULT_MAP_CENTER);
                        setMapZoom(DEFAULT_MAP_ZOOM);
                    }
                } catch (err) {
                    console.error("MapPage: Error fetching organization details:", err);
                    setError(`Failed to load organization details: ${err.message}`);
                    setOrganizationDetails({ orgName: "Default Location (Error)" });
                    setMapCenter(HARDCODED_DEFAULT_MAP_CENTER);
                    setMapZoom(DEFAULT_MAP_ZOOM);
                }
            };
            fetchOrganizationCenter();
        } else {
            setMapCenter(HARDCODED_DEFAULT_MAP_CENTER);
            setMapZoom(DEFAULT_MAP_ZOOM);
        }
    }, [userOrgId]);

    // Effect 2: Fetch Groups for the Organization
    useEffect(() => {
        if (loadingAuth || !currentUser || !userOrgId) {
            setLoadingGroups(loadingAuth || !userOrgId);
            if (!currentUser && !loadingAuth) setError("Please log in to view the map.");
            else if (!userOrgId && currentUser && !loadingAuth) setError("Organization details missing. Cannot fetch groups.");
            setGroups([]);
            return;
        }

        let isMounted = true;
        const fetchGroupsForOrg = async () => {
            setLoadingGroups(true); setError(null); setGroups([]);
            try {
                const groupCollectionRef = collection(db, 'group');
                const q = query(groupCollectionRef, where('orgId', '==', userOrgId));
                const groupsSnapshot = await getDocs(q);
                if (!isMounted) return;

                const groupsList = groupsSnapshot.docs.map(d => ({
                    ...d.data(),
                    id: d.id,  // Must come AFTER spread to ensure document ID isn't overwritten
                    name: d.data().groupName || `Group ${d.id.substring(0, 5)}...`,
                }));
                setGroups(groupsList);
            } catch (err) {
                console.error("Error fetching groups:", err);
                if (isMounted) setError(`Failed to load groups. ${err.message}`);
            } finally {
                if (isMounted) setLoadingGroups(false);
            }
        };
        fetchGroupsForOrg();
        return () => { isMounted = false; };
    }, [userOrgId, loadingAuth, currentUser]);

    // Effect 3: Fetch Members based on Selected Group
    const fetchMembers = useCallback(async () => {
        if (!selectedGroupId || !userOrgId) {
            setMembersData([]);
            setLoadingMembers(false);
            return;
        }
        setLoadingMembers(true); setError(null); setMembersData([]);
        try {
            const memberCollectionRef = collection(db, 'member');
            let memberDocs = [];
            if (selectedGroupId === 'all') {
                const q = query(memberCollectionRef, where('orgId', '==', userOrgId));
                const membersSnapshot = await getDocs(q);
                memberDocs = membersSnapshot.docs;
            } else {
                const roleCollectionRef = collection(db, 'role');
                const rolesQuery = query(roleCollectionRef, where('groupId', '==', selectedGroupId), where('orgId', '==', userOrgId));
                const rolesSnapshot = await getDocs(rolesQuery);
                const memberIds = rolesSnapshot.docs.map(d => d.data().memberId).filter(Boolean);
                if (memberIds.length === 0) {
                    setLoadingMembers(false);
                    return;
                }
                // Fetch members in parallel using Promise.all (avoids security rule issues with documentId() in queries)
                const memberPromises = memberIds.map(memberId =>
                    getDoc(doc(db, 'member', memberId)).catch(() => null)
                );
                const memberSnapshots = await Promise.all(memberPromises);
                memberSnapshots.forEach(memberSnap => {
                    if (memberSnap && memberSnap.exists() && memberSnap.data().orgId === userOrgId) {
                        memberDocs.push(memberSnap);
                    }
                });
            }
            const fetchedMembersData = memberDocs.map(d => ({ id: d.id, ...d.data() }));
            setMembersData(fetchedMembersData);
        } catch (err) {
            console.error("Error in fetchMembers:", err);
            setError(`Failed to load members. ${err.message}`);
            setMembersData([]);
        } finally {
            setLoadingMembers(false);
        }
    }, [selectedGroupId, userOrgId]);

    // Effect 4: Fetch Family Locations based on Fetched Members
    const fetchFamilies = useCallback(async () => {
        if (membersData.length === 0 || !userOrgId) {
            setFamiliesData([]);
            setLoadingFamilies(false);
            return;
        }
        setLoadingFamilies(true); setFamiliesData([]);
        try {
            const uniqueFamilyIds = membersData.reduce((acc, member) => {
                if (member.orgId === userOrgId && member.familyId && typeof member.familyId === 'string' && member.familyId.trim() !== '') {
                    acc.add(member.familyId.trim());
                }
                return acc;
            }, new Set());
            const familyIdsToFetch = Array.from(uniqueFamilyIds);
            if (familyIdsToFetch.length === 0) {
                setLoadingFamilies(false);
                return;
            }
            // Fetch families in parallel using Promise.all (much faster than sequential)
            const familyPromises = familyIdsToFetch.map(familyId =>
                getDoc(doc(db, 'family', familyId)).catch(() => null)
            );
            const familySnapshots = await Promise.all(familyPromises);
            let allValidFamilyData = [];
            familySnapshots.forEach(familyDocSnap => {
                if (familyDocSnap && familyDocSnap.exists()) {
                    const familyData = familyDocSnap.data();
                    if (familyData.orgId === userOrgId && familyData.lat != null && familyData.lng != null) {
                        allValidFamilyData.push({ id: familyDocSnap.id, ...familyData });
                    }
                }
            });
            setFamiliesData(allValidFamilyData);
            // Always center on church location if available
            const churchLat = organizationDetails?.lat;
            const churchLng = organizationDetails?.lng;
            if (allValidFamilyData.length > 0 && selectedGroupId) {
                if (churchLat != null && churchLng != null) {
                    // Center on church and zoom to fit most markers
                    setMapCenter({ lat: churchLat, lng: churchLng });
                    setMapZoom(calculateSmartZoom(churchLat, churchLng, allValidFamilyData));
                } else {
                    // No church location - center on first family
                    const firstFamily = allValidFamilyData[0];
                    setMapCenter({ lat: firstFamily.lat, lng: firstFamily.lng });
                    setMapZoom(allValidFamilyData.length > 1 ? 11 : 13);
                }
            } else if (!selectedGroupId && churchLat != null && churchLng != null) {
                setMapCenter({ lat: churchLat, lng: churchLng });
                setMapZoom(organizationDetails.defaultZoom || DEFAULT_MAP_ZOOM);
            }
        } catch (err) {
            console.error("Error in overall fetchFamilies:", err);
            setError(`Failed to load family locations. ${err.message}`);
            setFamiliesData([]);
        } finally {
            setLoadingFamilies(false);
        }
    }, [membersData, userOrgId, organizationDetails, selectedGroupId]);

    // Effect 5: Trigger Fetching Based on Dependencies
    useEffect(() => { fetchMembers(); }, [fetchMembers]);
    useEffect(() => { fetchFamilies(); }, [fetchFamilies]);

    // --- Event Handlers ---
    const handleGroupChange = (event) => {
        const newSelectedGroupId = event.target.value;
        setSelectedGroupId(newSelectedGroupId);
        setSelectedFamily(null);
        setSelectedOrganization(null);
        setError(null);
        setFamiliesData([]);
        setMembersData([]);
        if (!newSelectedGroupId && organizationDetails?.lat && organizationDetails?.lng) {
            setMapCenter({ lat: organizationDetails.lat, lng: organizationDetails.lng });
            setMapZoom(organizationDetails.defaultZoom || DEFAULT_MAP_ZOOM);
        }
    };

    const handleMarkerClick = (type, data) => {
        if (type === 'family') {
            setSelectedOrganization(null);
            setSelectedFamily(data);
            setMapCenter({ lat: data.lat, lng: data.lng });
        } else if (type === 'organization') {
            setSelectedFamily(null);
            setSelectedOrganization(data);
            if (data.lat && data.lng) {
                setMapCenter({ lat: data.lat, lng: data.lng });
            }
        }
    };

    const handleMapClick = () => {
        setSelectedFamily(null);
        setSelectedOrganization(null);
    };

    // --- Render Logic ---

    // Loading state
    const showInitialLoading = loadingAuth || (!organizationDetails && !error && userOrgId);
    if (showInitialLoading) {
        return (
            <div className="map-page">
                <div className="map-loading">
                    <div className="skeleton-header"></div>
                    <div className="skeleton-map"></div>
                </div>
            </div>
        );
    }

    // Not logged in
    if (!currentUser && !loadingAuth) {
        return (
            <div className="map-page">
                <div className="map-header">
                    <div className="container">
                        <div className="map-header-content">
                            <div className="map-header-left">
                                <h1 className="map-title">
                                    <FontAwesomeIcon icon={faMapMarkedAlt} className="map-title-icon" />
                                    Map
                                </h1>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="container">
                    <Alert variant="warning">
                        Please <Alert.Link as={Link} to="/login">log in</Alert.Link> to view the map.
                    </Alert>
                </div>
            </div>
        );
    }

    // No org
    if (!userOrgId && currentUser && !loadingAuth && !organizationDetails && !error) {
        return (
            <div className="map-page">
                <div className="map-header">
                    <div className="container">
                        <div className="map-header-content">
                            <div className="map-header-left">
                                <h1 className="map-title">
                                    <FontAwesomeIcon icon={faMapMarkedAlt} className="map-title-icon" />
                                    Map
                                </h1>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="container">
                    <Alert variant="warning">Organization details are not available for your account.</Alert>
                </div>
            </div>
        );
    }

    // Render the Map
    const renderMap = () => {
        if (loadError) {
            return (
                <Alert variant="danger" className="map-alert">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    Error loading Google Maps. Please check your API key and internet connection.
                </Alert>
            );
        }

        if (!isLoaded) {
            return (
                <div className="map-loading-overlay">
                    <Spinner animation="border" />
                    <span className="map-loading-text">Loading Map...</span>
                </div>
            );
        }

        const orgMarkerIcon = {
            url: ORG_ICON_PATH,
            scaledSize: new window.google.maps.Size(35, 35),
            origin: new window.google.maps.Point(0, 0),
            anchor: new window.google.maps.Point(17, 35),
        };

        const showDataLoadingIndicator = loadingMembers || loadingFamilies;

        return (
            <>
                {showDataLoadingIndicator && (
                    <div className="map-loading-overlay">
                        <Spinner animation="border" />
                        <span className="map-loading-text">Loading Locations...</span>
                    </div>
                )}
                <GoogleMap
                    mapContainerStyle={{ width: '100%', height: '100%' }}
                    center={mapCenter}
                    zoom={mapZoom}
                    options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: true }}
                    onClick={handleMapClick}
                >
                    {organizationDetails?.lat && organizationDetails?.lng && (
                        <Marker
                            key="organization-marker"
                            position={{ lat: organizationDetails.lat, lng: organizationDetails.lng }}
                            icon={orgMarkerIcon}
                            onClick={() => handleMarkerClick('organization', organizationDetails)}
                            zIndex={10}
                        />
                    )}
                    {!showDataLoadingIndicator && familiesData.map(family => (
                        <Marker
                            key={family.id}
                            position={{ lat: family.lat, lng: family.lng }}
                            onClick={() => handleMarkerClick('family', family)}
                        />
                    ))}
                    {selectedOrganization && organizationDetails?.lat && organizationDetails?.lng && (
                        <InfoWindow
                            position={{ lat: organizationDetails.lat, lng: organizationDetails.lng }}
                            onCloseClick={() => setSelectedOrganization(null)}
                        >
                            <div className="map-infowindow">
                                <h5 className="map-infowindow-title">
                                    {selectedOrganization.orgName || 'Organization Location'}
                                </h5>
                                <p className="map-infowindow-address">
                                    {selectedOrganization.orgStreet && <>{selectedOrganization.orgStreet}<br /></>}
                                    {(selectedOrganization.orgCity || selectedOrganization.orgState || selectedOrganization.orgZipcode) && (
                                        <>
                                            {selectedOrganization.orgCity || ''}
                                            {selectedOrganization.orgCity && selectedOrganization.orgState ? ', ' : ''}
                                            {selectedOrganization.orgState || ''}
                                            {selectedOrganization.orgZipcode ? ` ${selectedOrganization.orgZipcode}` : ''}
                                        </>
                                    )}
                                    {!selectedOrganization.orgStreet && !selectedOrganization.orgCity && !selectedOrganization.orgState && (
                                        <em>Address details not available.</em>
                                    )}
                                </p>
                            </div>
                        </InfoWindow>
                    )}
                    {selectedFamily && (
                        <InfoWindow
                            position={{ lat: selectedFamily.lat, lng: selectedFamily.lng }}
                            onCloseClick={() => setSelectedFamily(null)}
                        >
                            <div className="map-infowindow">
                                <h5 className="map-infowindow-title">
                                    {selectedFamily.surname ? `${selectedFamily.surname} Family` : 'Selected Location'}
                                </h5>
                                <p className="map-infowindow-address">
                                    {selectedFamily.street && <>{selectedFamily.street}<br /></>}
                                    {(selectedFamily.city || selectedFamily.state || selectedFamily.zipcode) && (
                                        <>
                                            {selectedFamily.city ? `${selectedFamily.city}, ` : ''}
                                            {selectedFamily.state || ''} {selectedFamily.zipcode || ''}
                                        </>
                                    )}
                                    {!selectedFamily.street && !selectedFamily.city && !selectedFamily.state && !selectedFamily.zipcode && (
                                        <em>No address available</em>
                                    )}
                                </p>
                                <Link to={`/families/${selectedFamily.id}`} className="map-infowindow-link">
                                    View Family Details
                                </Link>
                            </div>
                        </InfoWindow>
                    )}
                </GoogleMap>
            </>
        );
    };

    // Get selected group name for subtitle
    const getSelectedGroupName = () => {
        if (selectedGroupId === 'all') return 'All Members';
        if (selectedGroupId) {
            const group = groups.find(g => g.id === selectedGroupId);
            return group?.name || 'Selected Group';
        }
        return null;
    };

    return (
        <div className="map-page">
            {/* Page Header */}
            <div className="map-header">
                <div className="container">
                    <div className="map-header-content">
                        <div className="map-header-left">
                            <div>
                                <h1 className="map-title">
                                    <FontAwesomeIcon icon={faMapMarkedAlt} className="map-title-icon" />
                                    Map
                                </h1>
                                {getSelectedGroupName() && (
                                    <p className="map-subtitle">Showing: {getSelectedGroupName()}</p>
                                )}
                            </div>
                        </div>
                        <div className="map-group-selector">
                            <span className="map-group-label">
                                <FontAwesomeIcon icon={faHome} className="me-1" />
                                View Residences:
                            </span>
                            <select
                                className="map-group-select"
                                value={selectedGroupId}
                                onChange={handleGroupChange}
                                disabled={loadingAuth || loadingGroups || loadingMembers || loadingFamilies || !userOrgId || !organizationDetails}
                            >
                                <option value="">-- Select a Group --</option>
                                <option value="all">All Members</option>
                                {groups.map(group => (
                                    <option key={group.id} value={group.id}>{group.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container">
                {/* Error Alert */}
                {error && (
                    <Alert variant="danger" className="mb-3" onClose={() => setError(null)} dismissible>
                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                        {error}
                    </Alert>
                )}

                {/* Map Container */}
                <div className="map-container">
                    <div className="map-wrapper">
                        {userOrgId && organizationDetails && (!error || (error && (loadingFamilies || loadingMembers))) ? (
                            renderMap()
                        ) : (
                            !error && (
                                <div className="map-empty-message">
                                    Map data cannot be displayed. Ensure organization details are loaded and valid.
                                </div>
                            )
                        )}
                    </div>

                    {/* Map Footer with Stats */}
                    {selectedGroupId && !loadingFamilies && !loadingMembers && (
                        <div className="map-info-footer">
                            <div className="map-stats">
                                <div className="map-stat">
                                    <FontAwesomeIcon icon={faUsers} />
                                    <span className="map-stat-value">{membersData.length}</span>
                                    <span>members</span>
                                </div>
                                <div className="map-stat">
                                    <FontAwesomeIcon icon={faHome} />
                                    <span className="map-stat-value">{familiesData.length}</span>
                                    <span>locations shown</span>
                                </div>
                            </div>
                            {familiesData.length === 0 && (
                                <div className="map-empty-state">
                                    <FontAwesomeIcon icon={faHome} className="map-empty-icon" />
                                    <div className="map-empty-text">
                                        <strong>No family locations to display</strong>
                                        <p>Family addresses with valid coordinates will appear as pins on the map.{isAdmin ? ' Add addresses when editing family details.' : ''}</p>
                                    </div>
                                    <Link to="/families" className="map-empty-link">Go to Families</Link>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default MapPage;
